import { useState, useEffect, useRef, useCallback } from 'react';
import apiCoreAdapter from '../../../../hooks/apiCoreAdapter';
import {
  fetchSchedulesOnce as fetchSchedulesShared,
  fetchPatientsOnce as fetchPatientsShared,
  fetchSessionsOnce as fetchSessionsShared,
  invalidateSessionsCache,
  type SessionStatus,
} from '../data';
import { isoToLocalParts, addMinutesToTime, timeToMinutes, toTwo } from '../utils';
import { isoToZoneParts, localTzToUTC } from '../timeZoneHelpers';
import { BA_TIMEZONE } from '../../../../utils/timezone';
import { hasServerCounterpart, mergeTemplateWithOptimistic } from '../bookingUtils';
import { isBlockingSlot } from '../blockedSlots';
import { PsychologistFreeScheduleStatus } from '../../../SessionDebug';
import type { PreviewSchedule } from '../WorkSchedulePanel';

// ---------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------

export type Patient = {
  id?: string;
  name?: string;
  email: string;
  phone?: string;
  process?: string;
  processType?: string;
  timezone?: string;
  nationality?: string;
  hasNextSessionDate?: boolean;
};

export type WorkSchedule = {
  id: string | number;
  psychologistId: string | number;
  weekday: number;
  startTime: string;
  endTime: string;
  active: boolean;
  scheduleType?: 'standard' | 'reschedule_only';
};

export type SessionWithPsychologist = {
  id: string;
  psychologistId: string | number;
  date: string;
  time: string;
  modality: string;
  status: SessionStatus;
  patient: { name?: string; email: string; phone?: string; timezone?: string };
  specialty?: string;
  origISO?: string;
  duration?: number;
  process_id?: string;
  appointment_type?: string;
  /**
   * IDs únicos (asignados por el backend) de los slots de bloqueo de 30min que componen
   * este bloqueo. Ver misma propiedad en el tipo homónimo de `../data`.
   */
  blockageIds?: string[];
};

export type FullDayRecurrence = { date: string; reason: string; recurrenceId?: string };

export type DateSpecificSlot = {
  date: string;
  startTime: string;
  endTime: string;
  scheduleType?: 'standard' | 'reschedule_only';
};

export type ScheduleDirectInput = {
  id?: string;
  psychologistId: number | string;
  date: string;
  time: string;
  modality: string;
  patientName: string;
  patientEmail: string;
  patientPhone?: string;
  specialty?: string;
  status?: SessionStatus;
  process_id?: string;
  appointment_type?: string;
  duration?: number;
};

// ---------------------------------------------------------------------------
// Hook options and return type
// ---------------------------------------------------------------------------

export interface UseCalendarDataOptions {
  psychologist: { id: string | number; specialties: string[] };
  psychologistId: string | null | undefined;
  startOfWeek: Date;
  forceRefresh: boolean;
  editMode: boolean;
  selectedTimeZone: string;
  scheduleNextHourUpdate: () => void;
}

export interface UseCalendarDataReturn {
  // State
  sessions: SessionWithPsychologist[];
  setSessions: React.Dispatch<React.SetStateAction<SessionWithPsychologist[]>>;
  patients: Patient[];
  setPatients: React.Dispatch<React.SetStateAction<Patient[]>>;
  template: WorkSchedule[];
  setTemplate: React.Dispatch<React.SetStateAction<WorkSchedule[]>>;
  loading: boolean;
  refreshing: boolean;
  templateReady: boolean;
  setTemplateReady: React.Dispatch<React.SetStateAction<boolean>>;
  sessionsReady: boolean;
  initialGridReady: boolean;
  fullDayRecurrences: FullDayRecurrence[];
  dateSpecificSlots: DateSpecificSlot[];
  setDateSpecificSlots: React.Dispatch<React.SetStateAction<DateSpecificSlot[]>>;
  slotsLoadedWeekKey: string | null;
  panelPreviewSchedules: PreviewSchedule[] | null;
  setPanelPreviewSchedules: React.Dispatch<React.SetStateAction<PreviewSchedule[] | null>>;
  deletedScheduleIds: Set<string | number>;
  setDeletedScheduleIds: React.Dispatch<React.SetStateAction<Set<string | number>>>;
  // Cache refs needed externally
  patientsCacheRef: React.MutableRefObject<Patient[] | undefined>;
  patientsCachePsychIdRef: React.MutableRefObject<string | undefined>;
  schedulesCacheRef: React.MutableRefObject<WorkSchedule[] | undefined>;
  schedulesCachePsychIdRef: React.MutableRefObject<string | undefined>;
  recurrencesCacheRef: React.MutableRefObject<FullDayRecurrence[] | undefined>;
  recurrencesCachePsychIdRef: React.MutableRefObject<string | undefined>;
  sessionsCacheByWeekRef: React.MutableRefObject<Map<string, SessionWithPsychologist[]>>;
  pendingFetchesRef: React.MutableRefObject<Map<string, Promise<unknown>>>;
  availabilityCacheRef: React.MutableRefObject<Map<string, SessionWithPsychologist[]>>;
  dateSpecificSlotsCacheRef: React.MutableRefObject<Map<string, DateSpecificSlot[]>>;
  availabilityPendingRef: React.MutableRefObject<Map<string, Promise<SessionWithPsychologist[]>>>;
  // Functions
  loadData: () => Promise<void>;
  scheduleDirect: (input: ScheduleDirectInput) => { ok: boolean };
  fetchDateSpecificSlotsForWeek: (weekStart: Date) => Promise<DateSpecificSlot[]>;
  fetchPatientsOnce: (psychologistId: string) => Promise<Patient[]>;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function useCalendarData({
  psychologist,
  psychologistId,
  startOfWeek,
  forceRefresh,
  editMode,
  selectedTimeZone,
  scheduleNextHourUpdate,
}: UseCalendarDataOptions): UseCalendarDataReturn {
  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------
  const [sessions, setSessions] = useState<SessionWithPsychologist[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [template, setTemplate] = useState<WorkSchedule[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [templateReady, setTemplateReady] = useState<boolean>(false);
  const [sessionsReady, setSessionsReady] = useState<boolean>(false);
  const [fullDayRecurrences, setFullDayRecurrences] = useState<FullDayRecurrence[]>([]);
  const [dateSpecificSlots, setDateSpecificSlots] = useState<DateSpecificSlot[]>([]);
  const [slotsLoadedWeekKey, setSlotsLoadedWeekKey] = useState<string | null>(null);
  const [initialGridReady, setInitialGridReady] = useState<boolean>(false);
  const [panelPreviewSchedules, setPanelPreviewSchedules] = useState<PreviewSchedule[] | null>(null);
  const [deletedScheduleIds, setDeletedScheduleIds] = useState<Set<string | number>>(new Set());

  // -------------------------------------------------------------------------
  // Refs — caches
  // -------------------------------------------------------------------------
  const schedulesCachePsychIdRef = useRef<string | undefined>(undefined);
  const schedulesCacheRef = useRef<WorkSchedule[] | undefined>(undefined);
  const patientsCachePsychIdRef = useRef<string | undefined>(undefined);
  const patientsCacheRef = useRef<Patient[] | undefined>(undefined);
  const recurrencesCachePsychIdRef = useRef<string | undefined>(undefined);
  const recurrencesCacheRef = useRef<FullDayRecurrence[] | undefined>(undefined);
  const sessionsCacheByWeekRef = useRef<Map<string, SessionWithPsychologist[]>>(new Map());
  const pendingFetchesRef = useRef<Map<string, Promise<unknown>>>(new Map());
  const availabilityCacheRef = useRef<Map<string, SessionWithPsychologist[]>>(new Map());
  const availabilityPendingRef = useRef<Map<string, Promise<SessionWithPsychologist[]>>>(new Map());
  const dateSpecificSlotsCacheRef = useRef<Map<string, DateSpecificSlot[]>>(new Map());

  // Internal ref: keeps editMode up to date inside async callbacks without
  // causing them to be re-created on every render.
  const editModeRef = useRef(editMode);
  editModeRef.current = editMode;

  // -------------------------------------------------------------------------
  // Private fetch helpers
  // -------------------------------------------------------------------------

  const fetchSchedulesOnce = useCallback(async (psychId: string): Promise<WorkSchedule[]> => {
    if (schedulesCacheRef.current && schedulesCachePsychIdRef.current === psychId) {
      return schedulesCacheRef.current;
    }
    const key = `schedules:${psychId}`;
    const pending = pendingFetchesRef.current.get(key) as Promise<WorkSchedule[]> | undefined;
    if (pending) return await pending;

    const promise = (async () => {
      const tpl = await fetchSchedulesShared(psychId, selectedTimeZone);
      schedulesCacheRef.current = tpl as unknown as WorkSchedule[];
      schedulesCachePsychIdRef.current = psychId;
      pendingFetchesRef.current.delete(key);
      return tpl as unknown as WorkSchedule[];
    })();

    pendingFetchesRef.current.set(key, promise);
    return await promise;
  }, [selectedTimeZone]);

  const fetchPatientsOnce = useCallback(async (psychId: string): Promise<Patient[]> => {
    if (patientsCacheRef.current && patientsCachePsychIdRef.current === psychId) {
      return patientsCacheRef.current;
    }
    const key = `patients:${psychId}`;
    const pending = pendingFetchesRef.current.get(key) as Promise<Patient[]> | undefined;
    if (pending) return await pending;

    const promise = (async () => {
      const normalized = await fetchPatientsShared(psychId);
      patientsCacheRef.current = normalized as Patient[];
      patientsCachePsychIdRef.current = psychId;
      pendingFetchesRef.current.delete(key);
      return normalized as Patient[];
    })();

    pendingFetchesRef.current.set(key, promise);
    return await promise;
  }, []);

  const fetchOccupationRecurrencesOnce = useCallback(async (psychId: string): Promise<FullDayRecurrence[]> => {
    if (recurrencesCacheRef.current && recurrencesCachePsychIdRef.current === psychId) {
      return recurrencesCacheRef.current;
    }
    const key = `recurrences:${psychId}`;
    const pending = pendingFetchesRef.current.get(key) as Promise<FullDayRecurrence[]> | undefined;
    if (pending) return await pending;

    const promise = (async () => {
      let recurrencesResponse;
      try {
        recurrencesResponse = await apiCoreAdapter.getOccupationRecurrences(String(psychId));
      } catch (error) {
        console.error('Error fetching occupation recurrences:', error);
        recurrencesResponse = { success: false, recurrences: [] };
      }

      const blockedDates: FullDayRecurrence[] = [];
      if (recurrencesResponse.success && recurrencesResponse.recurrences) {
        for (const rec of recurrencesResponse.recurrences) {
          if (rec.is_full_day && rec.start_time) {
            try {
              const date = new Date(rec.start_time);
              const dateString = date.toISOString().split('T')[0];
              const reason = rec.reason || 'Día bloqueado';
              blockedDates.push({ date: dateString, reason, recurrenceId: rec.id });
            } catch (error) {
              console.error('Error parsing start_time:', rec.start_time, error);
            }
          }
        }
      }

      recurrencesCacheRef.current = blockedDates;
      recurrencesCachePsychIdRef.current = psychId;
      pendingFetchesRef.current.delete(key);
      return blockedDates;
    })();

    pendingFetchesRef.current.set(key, promise);
    return await promise;
  }, []);

  const fetchSessionsOnce = useCallback(async (
    psychId: string,
    startOfWeekDate: Date,
    endOfWeek: Date,
    weekKey: string,
  ): Promise<SessionWithPsychologist[]> => {
    if (!forceRefresh) {
      const cached = sessionsCacheByWeekRef.current.get(weekKey);
      if (cached) return cached;
    }

    const key = `sessions:${weekKey}`;
    const pending = pendingFetchesRef.current.get(key) as Promise<SessionWithPsychologist[]> | undefined;
    if (pending) return await pending;

    const promise = (async () => {
      const external = await fetchSessionsShared(
        psychId,
        startOfWeekDate,
        endOfWeek,
        weekKey,
        psychologist.specialties[0] || 'general',
      );
      sessionsCacheByWeekRef.current.set(weekKey, external as unknown as SessionWithPsychologist[]);
      pendingFetchesRef.current.delete(key);
      return external as unknown as SessionWithPsychologist[];
    })();

    pendingFetchesRef.current.set(key, promise);
    return await promise;
  }, [psychologist.specialties, forceRefresh]);

  // -------------------------------------------------------------------------
  // createSessionsFromNonAvailableSlots
  // -------------------------------------------------------------------------

  const createSessionsFromNonAvailableSlots = useCallback(
    async (weekKey: string, startOfWeekDate: Date): Promise<SessionWithPsychologist[]> => {
      const cached = availabilityCacheRef.current.get(weekKey);
      if (cached) {
        const cachedSlots = dateSpecificSlotsCacheRef.current.get(weekKey);
        if (cachedSlots) {
          setSlotsLoadedWeekKey(startOfWeekDate.toISOString());
          setDateSpecificSlots(cachedSlots);
        }
        return cached;
      }

      const pending = availabilityPendingRef.current.get(weekKey);
      if (pending) return await pending;

      const promise = (async () => {
        const endOfWeek = new Date(startOfWeekDate);
        endOfWeek.setDate(startOfWeekDate.getDate() + 7);

        const availability = await apiCoreAdapter.psychologistAvailability({
          from: startOfWeekDate.toISOString(),
          to: endOfWeek.toISOString(),
          source: 'psi_platform',
        });

        // Los slots marcados como vacaciones no se pintan como "Slot ocupado": las vacaciones ya se
        // ven por sus fechas (ver blockedSlots.ts).
        const nonAvailableSlotsRaw = availability.filter(isBlockingSlot);

        // Deduplicar por datetime
        const slotMap = new Map<string, typeof nonAvailableSlotsRaw[0]>();
        for (const s of nonAvailableSlotsRaw) {
          if (!slotMap.has(s.slot)) slotMap.set(s.slot, s);
        }
        const nonAvailableSlots = Array.from(slotMap.values());

        const createBlockedSessionFromGroup = (
          group: typeof nonAvailableSlots,
        ): SessionWithPsychologist => {
          const first = group[0];
          const parts = isoToLocalParts(first.slot);
          return {
            id: `blocked-${group.map(s => s.id).join('-')}`,
            psychologistId: psychologist.id,
            date: parts.date,
            time: parts.time,
            modality: 'online',
            status: 'blocked',
            patient: { name: first.reason || 'Slot ocupado', email: 'blocked@slot.com' },
            specialty: psychologist.specialties[0] || 'general',
            origISO: first.slot,
            duration: group.length * 30,
            appointment_type: first.appointment_type || undefined,
            blockageIds: group.map(s => s.id),
          };
        };

        const buildBlockedSessions = (slots: typeof nonAvailableSlots): SessionWithPsychologist[] => {
          const sorted = [...slots].sort(
            (a, b) => new Date(a.slot).getTime() - new Date(b.slot).getTime(),
          );

          const result: SessionWithPsychologist[] = [];
          let currentGroup: typeof sorted = [];

          for (const slot of sorted) {
            if (currentGroup.length === 0) {
              currentGroup.push(slot);
              continue;
            }

            const last = currentGroup[currentGroup.length - 1];
            const lastParts = isoToLocalParts(last.slot);
            const currentParts = isoToLocalParts(slot.slot);
            const lastMinutes = timeToMinutes(lastParts.time);
            const currentMinutes = timeToMinutes(currentParts.time);

            // Sólo se agrupan slots contiguos que además comparten origen (status + reason).
            // Sin esto, un bloqueo manual puntual se fusiona visualmente con el slot no
            // disponible siguiente por otro motivo (ej. fuera de horario laboral), mostrando
            // un rango bloqueado más ancho del que la psicóloga realmente ocupó.
            if (
              currentParts.date === lastParts.date &&
              currentMinutes === lastMinutes + 30 &&
              slot.status === last.status &&
              (slot.reason || null) === (last.reason || null)
            ) {
              currentGroup.push(slot);
            } else {
              result.push(createBlockedSessionFromGroup(currentGroup));
              currentGroup = [slot];
            }
          }

          if (currentGroup.length) {
            result.push(createBlockedSessionFromGroup(currentGroup));
          }

          return result;
        };

        const blockedSessions = buildBlockedSessions(nonAvailableSlots);

        // Extract one-time availability slots (specific-day overrides)
        const availableRaw = availability.filter(
          s =>
            s.status === PsychologistFreeScheduleStatus.AVAILABLE &&
            s.reason === 'one_time_availability',
        );
        // Deduplicar por datetime — el backend devuelve un slot por cada appointment_type
        const availableSlotMap = new Map<string, typeof availableRaw[0]>();
        for (const s of availableRaw) {
          if (!availableSlotMap.has(s.slot)) availableSlotMap.set(s.slot, s);
        }
        const availSorted = Array.from(availableSlotMap.values()).sort(
          (a, b) => new Date(a.slot).getTime() - new Date(b.slot).getTime(),
        );
        const availRanges: DateSpecificSlot[] = [];
        let curGroup: typeof availSorted = [];

        for (const slot of availSorted) {
          if (curGroup.length === 0) {
            curGroup.push(slot);
            continue;
          }
          const lastParts = isoToZoneParts(curGroup[curGroup.length - 1].slot, selectedTimeZone);
          const curParts = isoToZoneParts(slot.slot, selectedTimeZone);
          const lastType = curGroup[curGroup.length - 1].schedule_type ?? 'standard';
          const curType = slot.schedule_type ?? 'standard';

          if (
            curParts.date === lastParts.date &&
            timeToMinutes(curParts.time) === timeToMinutes(lastParts.time) + 30 &&
            curType === lastType
          ) {
            curGroup.push(slot);
          } else {
            const firstParts = isoToZoneParts(curGroup[0].slot, selectedTimeZone);
            const lastEndParts = isoToZoneParts(curGroup[curGroup.length - 1].slot, selectedTimeZone);
            const groupType = (
              curGroup[0].schedule_type === 'reschedule_only' ? 'reschedule_only' : 'standard'
            ) as 'standard' | 'reschedule_only';
            availRanges.push({
              date: firstParts.date,
              startTime: firstParts.time,
              endTime: addMinutesToTime(lastEndParts.time, 30),
              scheduleType: groupType,
            });
            curGroup = [slot];
          }
        }

        if (curGroup.length > 0) {
          const firstParts = isoToZoneParts(curGroup[0].slot, selectedTimeZone);
          const lastEndParts = isoToZoneParts(curGroup[curGroup.length - 1].slot, selectedTimeZone);
          const groupType = (
            curGroup[0].schedule_type === 'reschedule_only' ? 'reschedule_only' : 'standard'
          ) as 'standard' | 'reschedule_only';
          availRanges.push({
            date: firstParts.date,
            startTime: firstParts.time,
            endTime: addMinutesToTime(lastEndParts.time, 30),
            scheduleType: groupType,
          });
        }

        dateSpecificSlotsCacheRef.current.set(weekKey, availRanges);
        setSlotsLoadedWeekKey(startOfWeekDate.toISOString());
        setDateSpecificSlots(availRanges);

        availabilityCacheRef.current.set(weekKey, blockedSessions);
        availabilityPendingRef.current.delete(weekKey);
        return blockedSessions;
      })();

      availabilityPendingRef.current.set(weekKey, promise);
      return await promise;
    },
    [psychologist.id, psychologist.specialties],
  );

  // -------------------------------------------------------------------------
  // loadData
  // -------------------------------------------------------------------------

  const loadData = useCallback(async (): Promise<void> => {
    setRefreshing(true);
    try {
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      const weekKey = `${startOfWeek.toISOString().slice(0, 10)}_${endOfWeek.toISOString().slice(0, 10)}_${psychologist.id}`;

      const schedulesPromise = fetchSchedulesOnce(String(psychologist.id));
      const patientsPromise = fetchPatientsOnce(String(psychologist.id));
      const sessionsPromise = fetchSessionsOnce(String(psychologist.id), startOfWeek, endOfWeek, weekKey);
      const sessionsFromSlotsPromise = createSessionsFromNonAvailableSlots(weekKey, startOfWeek);
      const recurrencesPromise = fetchOccupationRecurrencesOnce(String(psychologistId));

      const [tpl, normalizedPatients, external, sessionsFromSlots, fullDayMap] = await Promise.all([
        schedulesPromise,
        patientsPromise,
        sessionsPromise,
        sessionsFromSlotsPromise,
        recurrencesPromise,
      ]);

      setFullDayRecurrences(fullDayMap);

      // Si estamos en edit mode con template ya cargado, preservamos optimistic updates.
      // Si el template está vacío (primer fetch entrando directo en edit mode, ej. post-migración), lo poblamos.
      setTemplate(prev => {
        if (editModeRef.current && prev.length > 0) return prev;
        return mergeTemplateWithOptimistic(tpl, prev);
      });
      setPatients(normalizedPatients as Patient[]);
      setTemplateReady(true);

      setSessions(prevSessions => {
        const allKeys = new Set<string>();

        const makeKey = (s: SessionWithPsychologist) =>
          s.status === 'blocked'
            ? `${s.date}-${s.time}`
            : `${s.date}-${s.time}-${s.patient.email}`;

        const result: SessionWithPsychologist[] = [];

        // 1. Server sessions (highest priority). No deduplicar entre sí: dos sesiones
        // reales del backend pueden compartir fecha-hora-email legítimamente (ej. una
        // cancelada y su reemplazo pagado en el mismo slot tras una reprogramación) y
        // ambas deben llegar al render — es la capa de renderizado (sessionsByDayHour /
        // getSessionsInSlot) la que ya sabe ignorar la cancelada y renderizar todas las
        // activas (incluso solapadas) en columnas.
        // Antes esto sí deduplicaba por clave, y si la cancelada aparecía primero en la
        // respuesta del backend, la sesión real quedaba descartada acá y desaparecía por
        // completo de la vista semanal (aunque sí se veía en diaria/mensual, que no hacen
        // este paso de dedup).
        for (const s of external) {
          allKeys.add(makeKey(s));
          result.push(s);
        }

        // 2. Blocked slots from the API, grouped by day
        const blockedSlotsByDate = new Map<string, SessionWithPsychologist[]>();
        for (const s of sessionsFromSlots) {
          if (!blockedSlotsByDate.has(s.date)) {
            blockedSlotsByDate.set(s.date, []);
          }
          const daySlots = blockedSlotsByDate.get(s.date);
          if (daySlots) {
            daySlots.push(s);
          }
        }

        for (const [date, blockedSlots] of Array.from(blockedSlotsByDate.entries())) {
          const fullDayRecurrence = fullDayMap.find(rec => rec.date === date);
          const isFullDayBlocked = !!fullDayRecurrence;

          if (isFullDayBlocked) {
            const activeSessions = result.filter(
              s =>
                s.date === date &&
                s.status !== 'blocked' &&
                ['active', 'settled', 'incomplete', 'scheduled'].includes(s.status),
            );

            if (activeSessions.length === 0) {
              const blockReason = fullDayRecurrence.reason || 'Día bloqueado';
              const fullDayBlocked: SessionWithPsychologist = {
                id: `blocked-full-day-${date}`,
                psychologistId: psychologist.id,
                date,
                time: '00:00',
                modality: 'online',
                status: 'blocked',
                patient: { name: blockReason, email: 'blocked@slot.com' },
                specialty: psychologist.specialties[0] || 'general',
                duration: 23 * 60,
              };
              const key = makeKey(fullDayBlocked);
              if (!allKeys.has(key)) {
                allKeys.add(key);
                result.push(fullDayBlocked);
              }
            } else {
              const sortedActiveSessions = [...activeSessions].sort(
                (a, b) => timeToMinutes(a.time) - timeToMinutes(b.time),
              );

              let currentTime = 0;

              for (const activeSession of sortedActiveSessions) {
                const sessionStart = timeToMinutes(activeSession.time);
                const sessionDuration = activeSession.duration || 60;
                const sessionEnd = sessionStart + sessionDuration;

                if (currentTime < sessionStart) {
                  const blockedDuration = sessionStart - currentTime;
                  if (blockedDuration >= 30) {
                    const blockedStartTime = `${toTwo(Math.floor(currentTime / 60))}:${toTwo(currentTime % 60)}`;
                    const blockedSession: SessionWithPsychologist = {
                      id: `blocked-${date}-${blockedStartTime}`,
                      psychologistId: psychologist.id,
                      date,
                      time: blockedStartTime,
                      modality: 'online',
                      status: 'blocked',
                      patient: { name: 'Slot bloqueado', email: 'blocked@slot.com' },
                      specialty: psychologist.specialties[0] || 'general',
                      duration: blockedDuration,
                    };
                    const key = makeKey(blockedSession);
                    if (!allKeys.has(key)) {
                      allKeys.add(key);
                      result.push(blockedSession);
                    }
                  }
                }

                currentTime = Math.max(currentTime, sessionEnd);
              }

              const endOfDay = 23 * 60;
              if (currentTime < endOfDay) {
                const blockedDuration = endOfDay - currentTime;
                if (blockedDuration >= 30) {
                  const blockedStartTime = `${toTwo(Math.floor(currentTime / 60))}:${toTwo(currentTime % 60)}`;
                  const blockedSession: SessionWithPsychologist = {
                    id: `blocked-${date}-${blockedStartTime}`,
                    psychologistId: psychologist.id,
                    date,
                    time: blockedStartTime,
                    modality: 'online',
                    status: 'blocked',
                    patient: { name: 'Slot bloqueado', email: 'blocked@slot.com' },
                    specialty: psychologist.specialties[0] || 'general',
                    duration: blockedDuration,
                  };
                  const key = makeKey(blockedSession);
                  if (!allKeys.has(key)) {
                    allKeys.add(key);
                    result.push(blockedSession);
                  }
                }
              }
            }
          } else {
            for (const s of blockedSlots) {
              const key = makeKey(s);
              if (!allKeys.has(key)) {
                allKeys.add(key);
                result.push(s);
              }
            }
          }
        }

        // 3. Previous local sessions (lowest priority). Si la sesión real ya llegó del
        // backend, la copia local de cuando se agendó no se muestra (ver hasServerCounterpart).
        for (const s of prevSessions) {
          const key = makeKey(s);
          if (!allKeys.has(key) && !hasServerCounterpart(s, external)) {
            allKeys.add(key);
            result.push(s);
          }
        }

        return result;
      });

      setSessionsReady(true);
    } catch {
      const localKey = `work_template_${psychologist.id}`;
      let tpl: WorkSchedule[] = [];
      try {
        const raw = localStorage.getItem(localKey);
        if (raw) {
          const list = JSON.parse(raw) as unknown[];
          if (Array.isArray(list)) {
            tpl = list.map((item, idx) => {
              const h = item as Record<string, unknown>;
              const id = typeof h.id !== 'undefined' ? String(h.id) : `local-${idx}`;
              const weekday = Number((h['diaSemana'] as unknown) ?? 0);
              const startTime = String((h['horaInicio'] as unknown) ?? '').slice(0, 5);
              const endTime = String((h['horaFin'] as unknown) ?? '').slice(0, 5);
              const active = Boolean((h['activo'] as unknown));
              return { id, psychologistId: psychologist.id, weekday, startTime, endTime, active } as WorkSchedule;
            });
          }
        }
      } catch { /* ignore parse errors */ }

      // Misma lógica que el fetch principal: permitir población inicial si el template está vacío.
      setTemplate(prev => {
        if (editModeRef.current && prev.length > 0) return prev;
        const tplIds = new Set(tpl.map(t => String(t.id)));
        const optimistic = prev.filter(t => !tplIds.has(String(t.id)));
        return optimistic.length > 0 ? [...tpl, ...optimistic] : tpl;
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [psychologist.id, startOfWeek, fetchSchedulesOnce, fetchPatientsOnce, fetchSessionsOnce, fetchOccupationRecurrencesOnce]);

  // -------------------------------------------------------------------------
  // scheduleDirect — optimistically add a session + invalidate week cache
  // -------------------------------------------------------------------------

  const scheduleDirect = useCallback((input: ScheduleDirectInput): { ok: boolean } => {
    const session: SessionWithPsychologist = {
      id: input.id || `${input.date}-${input.time}-${input.patientEmail}`,
      psychologistId: input.psychologistId,
      date: input.date,
      time: input.time,
      modality: input.modality,
      status: input.status || 'active',
      patient: { name: input.patientName, email: input.patientEmail, phone: input.patientPhone },
      specialty: input.specialty,
      origISO: (() => { try { return localTzToUTC(input.date, input.time, selectedTimeZone || BA_TIMEZONE).toISOString(); } catch { return `${input.date} ${input.time}:00`; } })(),
      process_id: input.process_id,
      appointment_type: input.appointment_type,
      duration: input.duration || 60,
    };

    setSessions(prev => [...prev, session]);

    const endOfWeekDate = new Date(startOfWeek);
    endOfWeekDate.setDate(startOfWeek.getDate() + 6);
    const weekKey = `${startOfWeek.toISOString().slice(0, 10)}_${endOfWeekDate.toISOString().slice(0, 10)}_${psychologist.id}`;
    sessionsCacheByWeekRef.current.delete(weekKey);
    pendingFetchesRef.current.delete(`sessions:${weekKey}`);
    invalidateSessionsCache(weekKey);

    return { ok: true };
  }, [startOfWeek, psychologist.id, selectedTimeZone]);

  // -------------------------------------------------------------------------
  // fetchDateSpecificSlotsForWeek
  // -------------------------------------------------------------------------

  const fetchDateSpecificSlotsForWeek = useCallback(
    async (weekStart: Date): Promise<DateSpecificSlot[]> => {
      const endOfWeek = new Date(weekStart);
      endOfWeek.setDate(weekStart.getDate() + 7);

      const availability = await apiCoreAdapter.psychologistAvailability({
        from: weekStart.toISOString(),
        to: endOfWeek.toISOString(),
        source: 'psi_platform',
      });

      const availableRaw = availability.filter(
        s =>
          s.status === PsychologistFreeScheduleStatus.AVAILABLE &&
          s.reason === 'one_time_availability',
      );
      // Deduplicar por datetime — el backend devuelve un slot por cada appointment_type
      const availableSlotMap = new Map<string, typeof availableRaw[0]>();
      for (const s of availableRaw) {
        if (!availableSlotMap.has(s.slot)) availableSlotMap.set(s.slot, s);
      }
      const availSorted = Array.from(availableSlotMap.values()).sort(
        (a, b) => new Date(a.slot).getTime() - new Date(b.slot).getTime(),
      );

      const availRanges: DateSpecificSlot[] = [];
      let curGroup: typeof availSorted = [];

      for (const slot of availSorted) {
        if (curGroup.length === 0) {
          curGroup.push(slot);
          continue;
        }
        const lastParts = isoToZoneParts(curGroup[curGroup.length - 1].slot, selectedTimeZone);
        const curParts = isoToZoneParts(slot.slot, selectedTimeZone);
        const lastType = curGroup[curGroup.length - 1].schedule_type ?? 'standard';
        const curType = slot.schedule_type ?? 'standard';

        if (
          curParts.date === lastParts.date &&
          timeToMinutes(curParts.time) === timeToMinutes(lastParts.time) + 30 &&
          curType === lastType
        ) {
          curGroup.push(slot);
        } else {
          const firstParts = isoToZoneParts(curGroup[0].slot, selectedTimeZone);
          const lastEndParts = isoToZoneParts(curGroup[curGroup.length - 1].slot, selectedTimeZone);
          const groupType = (
            curGroup[0].schedule_type === 'reschedule_only' ? 'reschedule_only' : 'standard'
          ) as 'standard' | 'reschedule_only';
          availRanges.push({
            date: firstParts.date,
            startTime: firstParts.time,
            endTime: addMinutesToTime(lastEndParts.time, 30),
            scheduleType: groupType,
          });
          curGroup = [slot];
        }
      }

      if (curGroup.length > 0) {
        const firstParts = isoToZoneParts(curGroup[0].slot, selectedTimeZone);
        const lastEndParts = isoToZoneParts(curGroup[curGroup.length - 1].slot, selectedTimeZone);
        const groupType = (
          curGroup[0].schedule_type === 'reschedule_only' ? 'reschedule_only' : 'standard'
        ) as 'standard' | 'reschedule_only';
        availRanges.push({
          date: firstParts.date,
          startTime: firstParts.time,
          endTime: addMinutesToTime(lastEndParts.time, 30),
          scheduleType: groupType,
        });
      }

      setDateSpecificSlots(availRanges);
      return availRanges;
    },
    [selectedTimeZone],
  );

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  // Primary data load: runs when loadData changes (startOfWeek, psychologist.id, etc.)
  // Skips while in edit mode to avoid resetting unsaved schedule changes.
  // Skips if the psychologist ID is empty (real psychologist not yet loaded).
  useEffect(() => {
    scheduleNextHourUpdate();
    if (editMode) return;
    if (!psychologist.id) {
      setLoading(false);
      setRefreshing(false);
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadData, scheduleNextHourUpdate, editMode, psychologist.id]);

  // Invalidate sessions cache and reload only when forceRefresh transitions false → true
  // (i.e., when the user exits edit mode). Skips initial mount and changes to other
  // deps like loadData or startOfWeek — the primary effect covers those cases.
  const prevForceRefreshRef = useRef(forceRefresh);
  useEffect(() => {
    const wasForceRefresh = prevForceRefreshRef.current;
    prevForceRefreshRef.current = forceRefresh;
    if (forceRefresh && !wasForceRefresh) {
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      const weekKey = `${startOfWeek.toISOString().slice(0, 10)}_${endOfWeek.toISOString().slice(0, 10)}_${psychologist.id}`;
      sessionsCacheByWeekRef.current.delete(weekKey);
      pendingFetchesRef.current.delete(`sessions:${weekKey}`);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRefresh, startOfWeek, psychologist.id, loadData]);

  // Delay initial grid render to let scroll position settle
  useEffect(() => {
    if (!loading && !initialGridReady) {
      const timer = window.setTimeout(() => setInitialGridReady(true), 500);
      return () => window.clearTimeout(timer);
    }
  }, [loading, initialGridReady]);

  // Clear panelPreviewSchedules when leaving edit mode
  useEffect(() => {
    if (!editMode) {
      setPanelPreviewSchedules(null);
    }
  }, [editMode]);

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------

  return {
    // State
    sessions,
    setSessions,
    patients,
    setPatients,
    template,
    setTemplate,
    setTemplateReady,
    loading,
    refreshing,
    templateReady,
    sessionsReady,
    initialGridReady,
    fullDayRecurrences,
    dateSpecificSlots,
    setDateSpecificSlots,
    slotsLoadedWeekKey,
    panelPreviewSchedules,
    setPanelPreviewSchedules,
    deletedScheduleIds,
    setDeletedScheduleIds,
    // Cache refs
    patientsCacheRef,
    patientsCachePsychIdRef,
    schedulesCacheRef,
    schedulesCachePsychIdRef,
    recurrencesCacheRef,
    recurrencesCachePsychIdRef,
    sessionsCacheByWeekRef,
    pendingFetchesRef,
    availabilityCacheRef,
    dateSpecificSlotsCacheRef,
    availabilityPendingRef,
    // Functions
    loadData,
    scheduleDirect,
    fetchDateSpecificSlotsForWeek,
    fetchPatientsOnce,
  };
}
